export interface Amenity {
  id: number;
  name: string;
  icon: string;
  color: string | null;
  detail: string | null;
}

export interface ServiceTime {
  /** "SUN 8:15AM" for the first time of a day, then "10:00AM" */
  time: string;
  /** Day abbreviation, always present, e.g. "SUN" */
  day: string;
  day_full: string;
  /** "8:15AM" */
  time_of_day: string;
  time_24: string;
  amenities: Amenity[];
}

export interface Congregation {
  id: number;
  name: string;
  display_name: string;
  /** Two-letter code for tight header space, e.g. "TR" */
  short_name: string | null;
  description: string | null;
  campus_slug: string | null;
  pastor: { name: string | null; image_url: string | null };
  /** "SUN 8:15AM, 10:00AM & 11:30AM" */
  service_times: string;
  services: ServiceTime[];
  special_service: unknown;
  staff_directory_url: string | null;
}

export interface LocationGroup {
  id: number;
  primary_congregation_id: number | null;
  phone: string;
  svg_url: string | null;
  address: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
  congregations: Congregation[];
}

export function getPrimary(group: LocationGroup): Congregation {
  if (group.primary_congregation_id) {
    const found = group.congregations.find((c) => c.id === group.primary_congregation_id);
    if (found) return found;
  }
  return group.congregations[0];
}

export function getSubCongregations(group: LocationGroup): Congregation[] {
  const primaryId = group.primary_congregation_id ?? group.congregations[0]?.id;
  return group.congregations.filter((c) => c.id !== primaryId);
}

/** The slug the cookie carries: MP's campus slug, else derived from the name. */
export function campusSlug(cong: Congregation): string {
  return cong.campus_slug ?? cong.name.toLowerCase().replace(/\s+/g, '-');
}

/**
 * The campus page on woodsidebible.org. MP's slug keeps accents
 * ("troy-español") while WordPress strips them ("troy-espanol").
 */
export function campusDetailsUrl(cong: Congregation, baseUrl: string): string {
  const slug = campusSlug(cong)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return `${baseUrl.replace(/\/$/, '')}/${slug}/`;
}

/** "6600 Rochester Road, Troy, MI 48085" */
export function formatAddress(group: LocationGroup): string {
  const street = [group.address, group.address_line_2].filter(Boolean).join(', ');
  const zip = group.zip.split('-')[0];
  const cityLine = [group.city, [group.state, zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [street, cityLine].filter(Boolean).join(', ');
}

/** Group a congregation's regular service times by day: [{ day: "SUN", times: ["8:15AM", ...] }] */
export function servicesByDay(cong: Congregation): { day: string; services: ServiceTime[] }[] {
  const out: { day: string; services: ServiceTime[] }[] = [];
  for (const svc of cong.services) {
    const last = out[out.length - 1];
    if (last && last.day === svc.day) last.services.push(svc);
    else out.push({ day: svc.day, services: [svc] });
  }
  return out;
}
